import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import { useDebounce } from '@/hooks/useDebounce';
import usePartner from '@/hooks/usePartner';
import { FuzzyMatchResultsProps } from '@/models/FuzzyMatchResults';
import { PartnerProps } from '@/models/Partner';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';

function FuzzyMatchModal() {
  const theme = useTheme();
  const { translatedText } = useContext(LocaleContext);
  const {
    partnerInfo,
    primaryContact,
    selectedProgramInfo,
    updatePartnerInfo,
    updatePartnerGroupInfo,
    updateIsNewPartner,
  } = useContext(PartnerIntakeContext);

  const { getPartnerNameFuzzy } = usePartner();

  const [modalOpen, setModalOpen] = useState(false);
  const [fuzzyMatches, setFuzzyMatches] = useState<FuzzyMatchResultsProps[]>([]);

  const handleFuzzyModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleFuzzyMatchSelect = useCallback(
    (partner: FuzzyMatchResultsProps) => {
      const partnerData = {
        ...partnerInfo,
        ...partner,
      } satisfies PartnerProps;

      updatePartnerInfo(partnerData);
      updatePartnerGroupInfo({ id: partner.partnerGroupId });
      updateIsNewPartner(false);
    },
    [partnerInfo, updateIsNewPartner, updatePartnerGroupInfo, updatePartnerInfo]
  );

  const fuzzySearchPartnerName = useDebounce(partnerInfo?.partnerName, 1000);

  useEffect(() => {
    const loadFuzzyMatches = async (
      searchName: string,
      programId: ProgramNames,
      stateId?: string | null,
      countryId?: number | null
    ) => {
      let fuzzyMatchResults = await getPartnerNameFuzzy(searchName, programId, stateId, countryId);
      if (fuzzyMatchResults.length > 0) {
        if (fuzzyMatchResults.length > 0) {
          fuzzyMatchResults = fuzzyMatchResults.filter((res: FuzzyMatchResultsProps) => {
            return (
              (selectedProgramInfo.id != ProgramNames.TreeCitiesWorld &&
                res.countyName != null &&
                res.countyName != undefined &&
                res.countyName != '') ||
              (selectedProgramInfo.id == ProgramNames.TreeCitiesWorld &&
                res.countryName != null &&
                res.countryName != undefined &&
                res.countryName != '')
            );
          });

          if (fuzzyMatchResults.length === 0) return;
          setModalOpen(true);
          setFuzzyMatches(fuzzyMatchResults);
        }
      }
    };

    if (
      fuzzySearchPartnerName != null &&
      primaryContact != null &&
      ((primaryContact.countryId == USCountryId && primaryContact.stateId != null) ||
        primaryContact.countryId != USCountryId)
    ) {
      void loadFuzzyMatches(
        fuzzySearchPartnerName,
        selectedProgramInfo.id,
        primaryContact.stateId,
        primaryContact.countryId
      );
    }
  }, [fuzzySearchPartnerName, getPartnerNameFuzzy, primaryContact, selectedProgramInfo.id]);

  return (
    <Dialog open={modalOpen} onClose={handleFuzzyModalClose}>
      <DialogTitleWithClose onClose={handleFuzzyModalClose}>
        {translatedText.signup.fuzzyMatchTitle}
      </DialogTitleWithClose>
      <DialogContent>
        <Typography>{translatedText.signup.fuzzyMatchMessage}</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {fuzzyMatches.map((match: FuzzyMatchResultsProps) => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
              key={match.id}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px',
                  backgroundColor: theme.palette.sandTint.main,
                  border: `1px solid ${theme.palette.sandShade1.main}`,
                  borderRadius: '4px',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box>
                    <Typography variant='overline2'>{selectedProgramInfo?.partnerTerm}</Typography>
                    <Typography color='textSecondary'>{match.partnerName}</Typography>
                  </Box>

                  {selectedProgramInfo.id === ProgramNames.TreeCitiesWorld ? (
                    <Box>
                      <Typography variant='overline2'>
                        {translatedText.address.labelCountry}
                      </Typography>
                      <Typography color='textSecondary'>{match.countryName}</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant='overline2'>
                        {translatedText.address.labelCounty}
                      </Typography>
                      <Typography color='textSecondary'>{match.countyName}</Typography>
                    </Box>
                  )}
                </Box>

                <Button
                  onClick={() => {
                    handleFuzzyMatchSelect(match);
                  }}
                  variant='secondary'
                  size='small'
                >
                  {translatedText.signup.fuzzyMatchSelect}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleFuzzyModalClose} variant='primary' size='large'>
          {translatedText.signup.fuzzyMatchContinue(selectedProgramInfo?.partnerTerm ?? '')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FuzzyMatchModal;
